import { Select } from '@wonderschool/common-base-ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { createSelector } from 'reselect';

import { routes } from '../../../config/routes';
import { addError } from '../../../helpers/errors';
import {
  billingIntervalsEqual,
  buildCadenceFinancialSummary,
  buildSuggestedItems,
  buildSuggestedItemsMathNote,
  buildTuitionCalculationsForMatches,
  buildTuitionConversionSummaries,
  calculateCadenceTransitionTotal,
  convertRecurringItemsForCadence,
  dollarsToCents,
  matchTuitionInvoiceItems,
  normalizeInvoiceItemIds,
  validateCadenceTransitionFinancials,
} from '../../../helpers/invoicePlans';
import { isItemOverDiscounted } from '../../../helpers/invoices';
import { useInvoiceEditDetails, useInvoiceEditItems, useInvoiceEditStudents } from '../../../hooks/useInvoiceEdit';
import { buildMarketplaceLink, getPaths } from '../../../navigation';

import ShowError from '../../Messages/ShowError';
import SectionCard from '../../Shared/SectionCard';

import './Billing.scss';
import InvoiceItemList from './InvoiceItemList';
import { StepsContainer } from './Steps';
import TuitionConversionBanner from './TuitionConversionBanner';
import { StudentsListWidget } from './Widgets';

const selectTuitionSettings = createSelector([(state) => state.organizations?.currentOrganization], (org) => {
  if (!org || !Object.prototype.hasOwnProperty.call(org, 'tuitionPrograms') || !Array.isArray(org.tuitionPrograms)) {
    return { available: false, programs: [] };
  }
  return { available: true, programs: org.tuitionPrograms };
});
const EMPTY_STUDENTS = [];

export default function StepInvoiceItemList({ onSave, isInvoice = true, editSession = null }) {
  const { t } = useTranslation();
  const persistedInvoiceItemsFromStore = useInvoiceEditItems();
  const persistedInvoiceItems = useMemo(() => persistedInvoiceItemsFromStore ?? [], [persistedInvoiceItemsFromStore]);
  const students = useInvoiceEditStudents();
  const details = useInvoiceEditDetails();
  const organizationStudents = useSelector((state) => state.students?.list ?? EMPTY_STUDENTS);
  const organization = useSelector((state) => state.organizations?.currentOrganization);
  const firstLocation = useSelector((state) => state.locations?.list?.[0]);
  const tuitionSettings = useSelector(selectTuitionSettings);
  const normalizedPersistedItemsRef = useRef(null);
  if (normalizedPersistedItemsRef.current === null) {
    normalizedPersistedItemsRef.current = normalizeInvoiceItemIds(persistedInvoiceItems);
  }
  const initialBaselineItemsRef = useRef(null);
  if (initialBaselineItemsRef.current === null) {
    initialBaselineItemsRef.current = editSession?.originalItems?.length
      ? editSession.originalItems
      : normalizedPersistedItemsRef.current;
    if (editSession && !editSession.originalItems?.length && initialBaselineItemsRef.current.length) {
      editSession.originalItems = initialBaselineItemsRef.current;
    }
  }
  const originalItemsRef = useRef(initialBaselineItemsRef.current);
  const initializedRef = useRef(Boolean(persistedInvoiceItems.length));
  const manuallyEditedRef = useRef(Boolean(editSession?.manuallyEdited));
  const convertedCadenceRef = useRef(editSession?.convertedCadence ?? null);
  const [draftInvoiceItems, setDraftInvoiceItems] = useState(normalizedPersistedItemsRef.current);
  const [invoiceItemListKey, setInvoiceItemListKey] = useState(0);
  const [errors, setErrors] = useState();

  const intervalChanged = Boolean(
    details?.id &&
      details.oldBillingInterval &&
      !billingIntervalsEqual(details.oldBillingInterval, details.billingInterval)
  );
  const studentsWithBirthdays = useMemo(
    () =>
      students.map((student) => {
        const currentStudent = organizationStudents.find((candidate) => candidate.id === student.id);
        return currentStudent ? { ...student, ...currentStudent } : student;
      }),
    [organizationStudents, students]
  );
  const tuitionItemMatch = useMemo(
    () => matchTuitionInvoiceItems(draftInvoiceItems, studentsWithBirthdays),
    [draftInvoiceItems, studentsWithBirthdays]
  );
  const recurringMoneyError = useMemo(() => {
    if (!intervalChanged) return null;
    const draftError = validateCadenceTransitionFinancials(
      draftInvoiceItems,
      details?.oldBillingInterval,
      details?.billingInterval
    );
    if (draftError) return draftError;
    // The conversion and the before/after summaries also read the persisted baseline rows,
    // so an unconvertible persisted amount must fail closed instead of throwing mid-render.
    const baselineError = validateCadenceTransitionFinancials(
      originalItemsRef.current,
      details?.oldBillingInterval,
      details?.billingInterval
    );
    return baselineError ? { ...baselineError, source: 'baseline' } : null;
  }, [details?.billingInterval, details?.oldBillingInterval, draftInvoiceItems, intervalChanged]);
  const suggestedItems = useMemo(
    () =>
      !isInvoice && !details?.id && tuitionSettings.available
        ? buildSuggestedItems(studentsWithBirthdays, tuitionSettings.programs, details?.billingInterval)
        : [],
    [details?.billingInterval, details?.id, isInvoice, studentsWithBirthdays, tuitionSettings]
  );

  useEffect(() => {
    if (!persistedInvoiceItems.length || initializedRef.current || manuallyEditedRef.current) return;
    const normalizedItems = normalizeInvoiceItemIds(persistedInvoiceItems);
    originalItemsRef.current = normalizedItems;
    if (editSession) editSession.originalItems = normalizedItems;
    setDraftInvoiceItems(normalizedItems);
    initializedRef.current = true;
    setInvoiceItemListKey((key) => key + 1);
  }, [editSession, persistedInvoiceItems]);

  useEffect(() => {
    if (initializedRef.current || manuallyEditedRef.current || !suggestedItems.length) return;
    const normalizedItems = normalizeInvoiceItemIds(suggestedItems);
    originalItemsRef.current = normalizedItems;
    if (editSession) editSession.originalItems = normalizedItems;
    setDraftInvoiceItems(normalizedItems);
    initializedRef.current = true;
    setInvoiceItemListKey((key) => key + 1);
  }, [editSession, suggestedItems]);

  useEffect(() => {
    const cadenceKey = `${details?.oldBillingInterval ?? ''}:${details?.billingInterval ?? ''}`;
    if (
      (details?.id && !initializedRef.current) ||
      recurringMoneyError ||
      (intervalChanged && tuitionItemMatch.status === 'ambiguous') ||
      convertedCadenceRef.current === cadenceKey
    ) {
      return;
    }

    const baselineWithStableStudentIds = mergeStableStudentAssignments(
      originalItemsRef.current,
      draftInvoiceItems,
      tuitionItemMatch.matches
    );
    const converted = intervalChanged
      ? convertRecurringItemsForCadence(
          baselineWithStableStudentIds,
          details.oldBillingInterval,
          details.billingInterval
        )
      : baselineWithStableStudentIds;
    convertedCadenceRef.current = cadenceKey;
    if (editSession) editSession.convertedCadence = cadenceKey;
    setDraftInvoiceItems(converted);
    setInvoiceItemListKey((key) => key + 1);
  }, [
    details?.billingInterval,
    details?.id,
    details?.oldBillingInterval,
    draftInvoiceItems,
    editSession,
    intervalChanged,
    recurringMoneyError,
    tuitionItemMatch,
  ]);

  const tuitionCalculations = useMemo(
    () =>
      intervalChanged && !recurringMoneyError && tuitionItemMatch.status === 'matched'
        ? buildTuitionCalculationsForMatches(
            tuitionItemMatch.matches.map((match) => ({
              ...match,
              baselineInvoiceItem: originalItemsRef.current.find((item) => item.id === match.invoiceItem.id),
              currentInvoiceItem: match.invoiceItem,
              student:
                studentsWithBirthdays.find((student) =>
                  [student.id, student.uid].filter(Boolean).includes(match.student.id ?? match.student.uid)
                ) ?? match.student,
            })),
            tuitionSettings.programs,
            details?.billingInterval,
            details?.oldBillingInterval,
            tuitionSettings.available
          )
        : [],
    [
      details?.billingInterval,
      details?.oldBillingInterval,
      intervalChanged,
      recurringMoneyError,
      studentsWithBirthdays,
      tuitionItemMatch.matches,
      tuitionItemMatch.status,
      tuitionSettings,
    ]
  );
  const financialSummary = useMemo(
    () =>
      intervalChanged && !recurringMoneyError && tuitionItemMatch.status !== 'ambiguous'
        ? buildCadenceFinancialSummary(
            originalItemsRef.current,
            draftInvoiceItems,
            details?.oldBillingInterval,
            details?.billingInterval
          )
        : null,
    [
      details?.billingInterval,
      details?.oldBillingInterval,
      draftInvoiceItems,
      intervalChanged,
      recurringMoneyError,
      tuitionItemMatch.status,
    ]
  );
  const conversionSummaries = useMemo(
    () =>
      buildTuitionConversionSummaries(tuitionCalculations, details?.billingInterval, details?.oldBillingInterval, t),
    [details?.billingInterval, details?.oldBillingInterval, t, tuitionCalculations]
  );
  const mathNote = useMemo(
    () =>
      !persistedInvoiceItems.length ? buildSuggestedItemsMathNote(suggestedItems, details?.billingInterval, t) : null,
    [details?.billingInterval, persistedInvoiceItems.length, suggestedItems, t]
  );
  const marketplaceSchoolId = organization?.marketplaceSchoolId ?? firstLocation?.marketplaceSchoolId;
  const tuitionSettingsUrl = marketplaceSchoolId
    ? buildMarketplaceLink({ organization, location: firstLocation, path: getPaths().external.tuitionSettings })
    : null;

  return <StepsContainer isInvoice={isInvoice} CurrentForm={InvoiceItemsForm} Widgets={Widgets} onNext={onSaveLocal} />;

  function Widgets() {
    return <StudentsListWidget students={students} allowDelete={false} />;
  }

  function InvoiceItemsForm() {
    return (
      <SectionCard header={t('Enter charges')} data-testid="enter-charges-label">
        <ShowError errors={errors} />
        {!isInvoice && details?.billingInterval && (
          <div
            className="mb-4 flex items-center justify-between rounded bg-gray-50 px-4 py-3"
            data-testid="charges-billing-interval"
          >
            <div>
              <div className="mb-0.5 text-xs text-gray-800">{t('Billing Interval')}</div>
              <strong data-testid="charges-billing-interval-value">{t(details.billingInterval)}</strong>
            </div>
            <Link
              data-testid="change-billing-interval-link"
              to={`${routes.billing.plans.edit}${routes.billing.hashes.details}`}
            >
              {t('billing.charges.changeIntervalInDetails')}
            </Link>
          </div>
        )}
        {intervalChanged && tuitionItemMatch.status === 'ambiguous' && (
          <StudentAssignments
            draftInvoiceItems={draftInvoiceItems}
            students={studentsWithBirthdays}
            onAssign={assignStudentToRow}
            t={t}
          />
        )}
        <TuitionConversionBanner
          billingInterval={details?.billingInterval}
          conversionSummaries={conversionSummaries}
          financialSummary={financialSummary}
          isManualOverride={manuallyEditedRef.current}
          mathNote={mathNote}
          tuitionSettingsUrl={tuitionSettingsUrl}
        />
        <InvoiceItemList
          key={invoiceItemListKey}
          invoiceItemList={draftInvoiceItems}
          isEditable={!(intervalChanged && tuitionItemMatch.status === 'ambiguous')}
          onChange={onChangeInvoiceItemList}
          financialError={recurringMoneyError}
          totalOverride={
            intervalChanged && !recurringMoneyError ? calculateCadenceTransitionTotal(draftInvoiceItems) : undefined
          }
        />
      </SectionCard>
    );
  }

  function validate() {
    let nextErrors = null;
    if (!draftInvoiceItems.length) {
      nextErrors = addError(nextErrors, 'invoiceItems', t('billing.stepInvoiceItemList.Invoicemusthaveatleastoneitem'));
    } else if (intervalChanged && recurringMoneyError) {
      if (recurringMoneyError.type === 'interval') {
        nextErrors = addError(
          nextErrors,
          'invoiceItems',
          t('billing.invoicePlan.errors.interval_invalid', {
            defaultValue: 'Choose a different supported billing interval.',
          })
        );
        return nextErrors;
      }
      const errorKey =
        recurringMoneyError.source === 'baseline'
          ? 'billing.tuitionAutofill.unconvertibleSavedRow'
          : recurringMoneyError.type === 'percentage'
            ? 'billing.tuitionAutofill.invalidPercentageRow'
            : 'billing.tuitionAutofill.invalidMoneyRow';
      nextErrors = addError(
        nextErrors,
        'invoiceItems',
        t(errorKey, {
          defaultValue:
            recurringMoneyError.source === 'baseline'
              ? "The saved amount for {{row}} is not a whole-cent value, so this plan's interval cannot be changed automatically."
              : recurringMoneyError.type === 'percentage'
                ? '{{row}} has a discount percentage that must be a valid number.'
                : '{{row}} needs a valid whole-cent amount.',
          row: recurringMoneyError.row.item || recurringMoneyError.row.description || t('Recurring charge'),
        })
      );
    } else if (draftInvoiceItems.some((item) => isCadenceItemOverDiscounted(item, intervalChanged))) {
      nextErrors = addError(nextErrors, 'invoiceItems', t('billing.invoice.itemNegativeError'));
    } else if (intervalChanged && tuitionItemMatch.status === 'ambiguous') {
      const unresolvedRow = draftInvoiceItems.find(
        (item) =>
          item.category === 'Tuition' &&
          !studentsWithBirthdays.some((student) => invoiceItemHasStudentId(item, student))
      );
      nextErrors = addError(
        nextErrors,
        'invoiceItems',
        unresolvedRow
          ? t('billing.tuitionAutofill.ambiguousRow', {
              defaultValue: '{{row}} needs a valid child assignment.',
              row: unresolvedRow.item || unresolvedRow.description || t('Tuition charge'),
            })
          : t('billing.tuitionAutofill.ambiguousCharge')
      );
    }
    return nextErrors;
  }

  function onSaveLocal() {
    const nextErrors = validate();
    if (nextErrors) {
      setErrors(nextErrors);
      return false;
    }
    onSave?.(draftInvoiceItems);
    return true;
  }

  function onChangeInvoiceItemList(invoiceItems) {
    manuallyEditedRef.current = true;
    if (editSession) editSession.manuallyEdited = true;
    setDraftInvoiceItems(invoiceItems);
  }

  function assignStudentToRow(rowId, studentId) {
    setDraftInvoiceItems((items) => items.map((item) => (item.id === rowId ? { ...item, studentId } : item)));
    setInvoiceItemListKey((key) => key + 1);
    setErrors(null);
  }
}

function mergeStableStudentAssignments(originalItems, draftItems, matches) {
  const currentById = new Map(draftItems.filter((item) => item.id).map((item) => [item.id, item]));
  const matchedStudentById = new Map(
    matches
      .map(({ invoiceItemIndex, student }) => [draftItems[invoiceItemIndex]?.id, student.id])
      .filter(([itemId]) => itemId)
  );
  const matchedStudentByIndex = new Map(matches.map(({ invoiceItemIndex, student }) => [invoiceItemIndex, student.id]));
  const canUseSameCollectionIndex = originalItems.length === draftItems.length;

  return originalItems.map((originalItem, index) => {
    const currentItem = originalItem.id
      ? currentById.get(originalItem.id)
      : canUseSameCollectionIndex
        ? draftItems[index]
        : undefined;
    const studentId =
      currentItem?.studentId ??
      (originalItem.id ? matchedStudentById.get(originalItem.id) : matchedStudentByIndex.get(index));
    const restoredItem = {
      ...originalItem,
      discounts: originalItem.discounts?.map((discount) => ({ ...discount })),
    };

    return studentId ? { ...restoredItem, studentId } : restoredItem;
  });
}

function isCadenceItemOverDiscounted(item, intervalChanged) {
  if (intervalChanged && dollarsToCents(item.amount) < 0 && !(item.discounts ?? []).length) return false;
  return isItemOverDiscounted(item);
}

function invoiceItemHasStudentId(invoiceItem, student) {
  const validStudentIds = [student.id, student.uid].filter(Boolean);
  const invoiceItemStudentIds = [
    invoiceItem.studentId,
    invoiceItem.studentUid,
    invoiceItem.student?.id,
    invoiceItem.student?.uid,
  ].filter(Boolean);
  return validStudentIds.some((studentId) => invoiceItemStudentIds.includes(studentId));
}

function StudentAssignments({ draftInvoiceItems, students, onAssign, t }) {
  const options = students.map((student) => ({ value: student.id, label: student.displayName }));
  return (
    <div className="mb-4 grid gap-3 rounded bg-gray-50 p-4" data-testid="tuition-student-assignments">
      <strong>{t('billing.tuitionAutofill.assignStudentsTitle')}</strong>
      {draftInvoiceItems
        .filter((item) => item.category === 'Tuition')
        .map((item, index) => (
          <div className="grid items-center gap-3 sm:grid-cols-2" key={item.id}>
            <span>
              {t('billing.tuitionAutofill.assignmentRowSummary', {
                amount: Number(item.amount).toFixed(2),
                row: item.item || item.description || t('Tuition {{index}}', { index: index + 1 }),
              })}
            </span>
            <Select
              name={`tuition-student-${item.id}`}
              options={options}
              value={item.studentId ?? ''}
              placeholder={t('billing.tuitionAutofill.selectStudent')}
              onChange={(change) => onAssign(item.id, change.value)}
            />
          </div>
        ))}
    </div>
  );
}
